import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SparklesIcon, ClockIcon, BadgeCheckIcon, ZapIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
const PROMPT_LABS_IMG = 'https://cdn.magicpatterns.com/uploads/5Q9RfJNAqY4CyZrnPn4qcW/mk-principal.png?w=640&q=72&fm=webp';
const TOOLS = ['ChatGPT', 'Midjourney', 'Gemini', 'Claude', 'Meta Ads', 'TikTok Ads', 'y más'];
function RotatingTools() {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setIndex(prev => (prev + 1) % TOOLS.length);
    }, 1800);
    return () => clearInterval(id);
  }, []);
  return <div className="mt-8 flex flex-col items-center gap-2" data-id="element-104">
      <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-silver/50" data-id="element-105">
        Usado por creadores en
      </span>
      <div className="flex h-7 items-center justify-center overflow-hidden" data-id="element-106">
        <AnimatePresence mode="wait" data-id="element-107">
          <motion.span key={index} initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -14
        }} transition={{
          duration: 0.4,
          ease: 'easeOut'
        }} className="font-heading text-lg font-extrabold text-accent-light sm:text-xl" data-id="element-108">
            {TOOLS[index]}
          </motion.span>
        </AnimatePresence>
      </div>
    </div>;
}
export function Hero() {
  return <header className="relative w-full overflow-hidden bg-ink-950" data-id="element-109">
      <div className="absolute inset-0 bg-lab-grid opacity-40" aria-hidden="true" data-id="element-110" />
      <div className="absolute inset-0 bg-radial-cyan" aria-hidden="true" data-id="element-111" />
      {/* floating glows — hidden on mobile to lighten GPU paint cost */}
      <div className="pointer-events-none absolute -left-24 top-24 hidden h-72 w-72 rounded-full bg-accent/10 blur-3xl sm:block" aria-hidden="true" data-id="element-112" />
      <div className="pointer-events-none absolute -right-20 bottom-10 hidden h-72 w-72 rounded-full bg-accent/10 blur-3xl sm:block" aria-hidden="true" data-id="element-113" />

      <div className="relative mx-auto flex max-w-3xl flex-col items-center px-5 pb-16 pt-12 text-center lg:pb-24 lg:pt-16" data-id="element-114">
        <div className="flex flex-col items-center" data-id="element-115">
          <motion.div initial={{
          opacity: 0,
          y: -12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-accent-light" data-id="element-116">
            <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-117" />
            Nano-Creative Labs
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.1
        }} className="mt-5 font-display text-4xl uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl" data-id="element-118">
            <span className="text-chrome" data-id="element-119">
              Cómo materializar cualquier idea, producto o anuncio en{' '}
            </span>
            <span className="text-cyan-gradient" data-id="element-120">60 segundos</span>
            <span className="text-chrome" data-id="element-121"> sin ser un experto en IA</span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.25
        }} className="mt-5 text-base text-silver/80 sm:text-lg" data-id="element-122">
            — Incluso si nunca has logrado que ChatGPT te dé una respuesta útil.
          </motion.p>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.35
        }} className="mt-4 text-sm leading-relaxed text-silver/70 sm:text-base" data-id="element-123">
            Deja de "jugar" con la Inteligencia Artificial y empieza a usarla
            como una{' '}
            <span className="font-semibold text-white" data-id="element-124">
              fábrica de ejecución
            </span>
            . Domina el sistema de Nano-Creative Labs para crear imágenes
            ultra-reales, ofertas irresistibles y copys que venden por ti.
          </motion.p>

          {/* Hero product image — sits below the text box, above the CTA */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="relative mx-auto mt-10 w-full max-w-xs sm:max-w-sm" data-id="element-125">
            <div className="absolute inset-0 -z-10 scale-90 rounded-full bg-accent/20 blur-3xl" aria-hidden="true" data-id="element-126" />
            <motion.img src={PROMPT_LABS_IMG} alt="Ebook PROMPT LABS — The Art & Science of AI Prompt Engineering" width={640} height={640} className="w-full rounded-2xl shadow-card ring-1 ring-white/10 animate-float-slow" loading="eager" fetchPriority="high" decoding="async" data-id="element-127" />

            {/* Floating label — top right: tiempo de ejecución */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.8,
            y: -8
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.9
          }} className="absolute -right-2 -top-3 flex items-center gap-2 rounded-xl border border-accent/30 bg-ink-900/90 px-3 py-2 shadow-glow-cyan backdrop-blur-md sm:-right-4" data-id="element-128">
              <ZapIcon className="h-4 w-4 text-accent" aria-hidden="true" data-id="element-129" />
              <span className="text-left leading-tight" data-id="element-130">
                <span className="block font-heading text-sm font-extrabold text-white" data-id="element-131">
                  60 s
                </span>
                <span className="block text-[10px] uppercase tracking-widest text-silver/60" data-id="element-132">
                  Tiempo de ejecución
                </span>
              </span>
            </motion.div>

            {/* Floating label — bottom left: acceso de por vida */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.8,
            y: 8
          }} animate={{
            opacity: 1,
            scale: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 1.05
          }} className="absolute -bottom-3 -left-2 flex items-center gap-2 rounded-xl border border-accent/30 bg-ink-900/90 px-3 py-2 shadow-glow-cyan backdrop-blur-md sm:-left-4" data-id="element-133">
              <BadgeCheckIcon className="h-4 w-4 text-accent" aria-hidden="true" data-id="element-134" />
              <span className="text-left leading-tight" data-id="element-135">
                <span className="block font-heading text-sm font-extrabold text-white" data-id="element-136">
                  De por vida
                </span>
                <span className="block text-[10px] uppercase tracking-widest text-silver/60" data-id="element-137">
                  Acceso
                </span>
              </span>
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.5
        }} className="mt-10 w-full" data-id="element-138">
            <CtaButton data-id="element-139" />
          </motion.div>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-silver/60" data-id="element-140">
            <span className="inline-flex items-center gap-1.5" data-id="element-141">
              <BadgeCheckIcon className="h-4 w-4 text-accent" aria-hidden="true" data-id="element-142" />{' '}
              Acceso de por vida
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-143">
              <ClockIcon className="h-4 w-4 text-accent" aria-hidden="true" data-id="element-144" />{' '}
              Acceso inmediato
            </span>
            <span className="inline-flex items-center gap-1.5" data-id="element-145">
              <BadgeCheckIcon className="h-4 w-4 text-accent" aria-hidden="true" data-id="element-146" />{' '}
              Garantía de 30 días
            </span>
          </div>

          <RotatingTools data-id="element-147" />
        </div>
      </div>
    </header>;
}